import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getOffboardingInfo, startOffboarding } from "../api/offboarding";
import type {
  OffboardingStep,
  StartOffboardingDto,
  StartOffboardingRequest,
} from "../types/offboarding-types";

export function useOrganizationOffboarding() {
  const [step, setStep] = useState<OffboardingStep>("intro");
  const [error, setError] = useState<string | null>(null);

  // confirm modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userConfirmText, setUserConfirmText] = useState("");

  const { data: offboardingInfo, isLoading } = useQuery({
    queryKey: ["offboarding-info"],
    queryFn: getOffboardingInfo,
  });

  const orgList = useMemo(() => offboardingInfo?.org_list ?? [], [offboardingInfo]);

  const [form, setForm] = useState<StartOffboardingDto>({
    org_name: "",
  });

  const isConfirmed = userConfirmText === form.org_name;

  useEffect(() => {
    if (orgList.length > 0 && !form.org_name) {
      setForm((prev: StartOffboardingDto) => ({
        ...prev,
        org_name: orgList[0],
      }));
    }
  }, [orgList, form.org_name]);

  const offboardingMutation = useMutation({
    mutationFn: (request: StartOffboardingRequest) => startOffboarding(request),
    onSuccess: () => {
      setError(null);
      setStep("done");
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "오프보딩 중 오류가 발생했습니다.");
      setStep("done");
    },
  });

  const openConfirm = () => {
    if (!form.org_name) return { ok: false as const, error: "조직을 선택해 주세요." };
    setUserConfirmText("");
    setConfirmOpen(true);
    return { ok: true as const };
  };

  const start = async () => {
    setConfirmOpen(false);
    setStep("offboarding");
    setError(null);

    const request: StartOffboardingRequest = {
      org_name: form.org_name,
    };

    try {
      await offboardingMutation.mutateAsync(request);
    } catch (err) {
      console.error("Offboarding error:", err);
    }
  };

  const finish = () => {
    setError(null);
    setStep("intro");
  };

  return {
    orgList,

    step,
    setStep,

    form,
    setForm,

    confirmOpen,
    setConfirmOpen,
    userConfirmText,
    setUserConfirmText,
    isConfirmed,

    error,

    openConfirm,
    start,
    finish,

    isLoading,
    isProcessing: offboardingMutation.isPending,
  };
}
